import type { AccountResponse } from '../../account/AccountResponse';

// Mirrors the backend AdminResource enum / AdminPermissions model (packages/llm-common).
export enum AdminResource {
  DASHBOARD = 'DASHBOARD',
  REPORTS = 'REPORTS',
  SETTINGS = 'SETTINGS',
  SOURCE_VERIFICATION = 'SOURCE_VERIFICATION',
  ACCOUNTS = 'ACCOUNTS',
  NOTIFICATIONS = 'NOTIFICATIONS',
  PERMISSIONS = 'PERMISSIONS',
  INTERCOM = 'INTERCOM',
  COSTS = 'COSTS',
  CLI_COMMANDS = 'CLI_COMMANDS',
  /** Plan templates (model + addon sets): read = see and apply, write = create / edit / delete. */
  PLAN_TEMPLATES = 'PLAN_TEMPLATES',
}

export type AccessMode = 'read' | 'write';

export interface ResourceAccess {
  read: boolean;
  write: boolean;
}

export type AdminPermissions = Record<AdminResource, ResourceAccess>;

export interface SuperadminWithPermissions {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  permissions: AdminPermissions;
}

// Resources editable from the Permissions tab (in display order). Only ACCOUNTS and PERMISSIONS have a
// meaningful `write`; the view-only resources expose `read` alone.
export const MANAGED_RESOURCES: { resource: AdminResource; label: string; hasWrite: boolean }[] = [
  { resource: AdminResource.ACCOUNTS, label: 'Accounts', hasWrite: true },
  { resource: AdminResource.DASHBOARD, label: 'Dashboard', hasWrite: false },
  { resource: AdminResource.REPORTS, label: 'Reports', hasWrite: false },
  { resource: AdminResource.SETTINGS, label: 'Settings', hasWrite: false },
  { resource: AdminResource.SOURCE_VERIFICATION, label: 'Source Verification', hasWrite: false },
  { resource: AdminResource.NOTIFICATIONS, label: 'Notifications', hasWrite: true },
  { resource: AdminResource.PERMISSIONS, label: 'Permissions', hasWrite: true },
  { resource: AdminResource.INTERCOM, label: 'Intercom', hasWrite: false },
  { resource: AdminResource.COSTS, label: 'Costs', hasWrite: true },
  { resource: AdminResource.CLI_COMMANDS, label: 'CLI commands', hasWrite: false },
  { resource: AdminResource.PLAN_TEMPLATES, label: 'Plan templates', hasWrite: true },
];

// Costs is granted and revoked by user 1 only (backend keeps the stored flags for anyone else) — the
// Permissions tab shows the Costs toggle to that user alone. Mirrors COSTS_GRANTOR_USER_ID in llm-common.
export const COSTS_GRANTOR_USER_ID = 1;

// Effective permissions for a superadmin with no stored row: full access everywhere. Matches the backend default.
export const defaultAdminPermissions = (): AdminPermissions => ({
  [AdminResource.DASHBOARD]: { read: true, write: true },
  [AdminResource.REPORTS]: { read: true, write: true },
  [AdminResource.SETTINGS]: { read: true, write: true },
  [AdminResource.SOURCE_VERIFICATION]: { read: true, write: true },
  [AdminResource.ACCOUNTS]: { read: true, write: true },
  [AdminResource.NOTIFICATIONS]: { read: true, write: true },
  [AdminResource.PERMISSIONS]: { read: true, write: true },
  [AdminResource.INTERCOM]: { read: true, write: true },
  [AdminResource.COSTS]: { read: true, write: true },
  [AdminResource.CLI_COMMANDS]: { read: true, write: true },
  [AdminResource.PLAN_TEMPLATES]: { read: true, write: true },
});

// Reads the effective admin permissions from the account response (defaults to full access).
export const getAdminPermissions = (account: AccountResponse | null): AdminPermissions => {
  const stored = account?.superadminPermissions;
  const permissions = defaultAdminPermissions();
  if (!stored) return permissions;
  for (const resource of Object.values(AdminResource)) {
    if (stored[resource]) {
      permissions[resource] = stored[resource];
    }
  }
  return permissions;
};
